import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default slides as fallback
const defaultSlides = [
  {
    id: 'default-1',
    image: 'https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Discover Paradise',
    subtitle: 'Explore the Maldives',
    description: 'Crystal clear waters and pristine beaches await your arrival',
    cta_text: 'View Packages',
    cta_link: '/tours?destination=Maldives'
  },
  {
    id: 'default-2',
    image: 'https://images.pexels.com/photos/2356045/pexels-photo-2356045.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Alpine Adventures',
    subtitle: 'Swiss Alps Experience',
    description: 'Breathtaking mountain views and unforgettable experiences',
    cta_text: 'Explore Tours',
    cta_link: '/tours?destination=Switzerland'
  },
  {
    id: 'default-3',
    image: 'https://images.pexels.com/photos/1592461/pexels-photo-1592461.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Tropical Escape',
    subtitle: 'Bali Island Hopping',
    description: 'Temples, rice terraces, and vibrant culture in one destination',
    cta_text: 'Book Now',
    cta_link: '/tours?destination=Bali'
  },
  {
    id: 'default-4',
    image: 'https://images.pexels.com/photos/2070033/pexels-photo-2070033.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Cultural Journey',
    subtitle: 'Incredible India',
    description: 'Experience the rich heritage and diverse landscapes of India',
    cta_text: 'Start Journey',
    cta_link: '/tours?category=Domestic'
  }
];

export function HeroSlider() {
  const [slides, setSlides] = useState(defaultSlides);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch slides from API
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/slides`);
        if (response.data && response.data.length > 0) {
          setSlides(response.data);
        }
      } catch (error) {
        console.error('Error fetching slides:', error);
        // Keep default slides on error
      } finally {
        setLoading(false);
      }
    };
    fetchSlides();
  }, []);

  const goToSlide = useCallback((index) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 700);
  }, [isAnimating]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % slides.length);
  }, [currentSlide, goToSlide, slides.length]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  }, [currentSlide, goToSlide, slides.length]);

  useEffect(() => {
    if (!isPlaying || slides.length <= 1) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isPlaying, nextSlide, slides.length]);

  // Handle case when slides change and current index is out of bounds
  useEffect(() => {
    if (currentSlide >= slides.length) {
      setCurrentSlide(0);
    }
  }, [slides.length, currentSlide]);

  if (loading) {
    return (
      <section className="relative h-[100vh] min-h-[600px] bg-[#004E64] flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-white border-t-transparent rounded-full" />
      </section>
    );
  }

  return (
    <section className="relative h-[100vh] min-h-[600px] overflow-hidden" data-testid="hero-slider">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={cn(
            'absolute inset-0 transition-all duration-700 ease-in-out',
            index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
          )}
        >
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          </div>

          {/* Content */}
          <div className="relative h-full max-w-7xl mx-auto px-4 flex items-center">
            <div className="max-w-2xl">
              {slide.subtitle && (
                <span
                  className={cn(
                    'inline-block text-[#FF6B35] text-lg font-medium tracking-wider uppercase mb-4',
                    'transform transition-all duration-700 delay-100',
                    index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                  )}
                >
                  {slide.subtitle}
                </span>
              )}
              <h1
                className={cn(
                  'text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight',
                  'transform transition-all duration-700 delay-200',
                  index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                )}
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {slide.title}
              </h1>
              {slide.description && (
                <p
                  className={cn(
                    'text-xl text-white/80 mb-8 max-w-lg',
                    'transform transition-all duration-700 delay-300',
                    index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                  )}
                >
                  {slide.description}
                </p>
              )}
              <div
                className={cn(
                  'flex gap-4',
                  'transform transition-all duration-700 delay-400',
                  index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                )}
              >
                <Link to={slide.cta_link || '/tours'}>
                  <Button 
                    size="lg" 
                    className="bg-[#FF6B35] hover:bg-[#E85A2D] text-white rounded-full px-8 py-6 text-lg group"
                  >
                    {slide.cta_text || 'View Packages'}
                    <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-white text-white hover:bg-white hover:text-[#004E64] rounded-full px-8 py-6 text-lg"
                  >
                    Get Quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all z-10"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all z-10"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Bottom Controls */}
      {slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-6 z-10">
          {/* Dots */}
          <div className="flex gap-3">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  index === currentSlide
                    ? 'w-8 bg-[#FF6B35]'
                    : 'w-2 bg-white/50 hover:bg-white/80'
                )}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Play/Pause */}
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="w-10 h-10 bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all"
            aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
        </div>
      )}

      {/* Slide Counter */}
      {slides.length > 1 && (
        <div className="absolute bottom-8 right-8 text-white/60 text-sm hidden md:block">
          <span className="text-white font-bold text-xl">{String(currentSlide + 1).padStart(2, '0')}</span>
          <span className="mx-2">/</span>
          <span>{String(slides.length).padStart(2, '0')}</span>
        </div>
      )}
    </section>
  );
}
